import React from 'react';
import { GetServerSideProps } from 'next';
import { getCachedExpertAdviceFooterData, getCachedTopicData } from 'libs/contentful/api';
import type { WModuleGroupView } from '@zola/svc-web-api-ts-client';

import { useIsPostAuthNav3MobileOrDesktop } from 'hooks/useIsPostAuthNav3Active';
import { ExpertAdviceFooterType, TopicType } from 'libs/contentful/types';
import { fetchModuleGroups } from 'libs/server/moduleGroups';
import { fetchNavHTML } from 'libs/server/nav';
import { fetchUser } from 'libs/server/users';
import { badRequest } from 'libs/universal/errors';
import { notEmpty } from 'libs/universal/utils/type';
import { UserContext } from '@zola-helpers/client/dist/es/@types';
import logger from '@zola-helpers/server/dist/es/logger';

import Nav from 'components/Nav';
import MarketingModuleSection from 'components/expert-advice/MarketingModuleSection';
import ExpertAdviceHeader from 'components/expert-advice/ExpertAdviceHeader';
import TopicPage from 'components/expert-advice/TopicPage';

import { EA_PATH, EA_PATH_TOPIC } from 'constants/expertAdvice';
import { TRACKING_SECTIONS_EA } from 'constants/tracking';
import { PrimaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';

type TopicProps = {
  expertAdviceFooter: ExpertAdviceFooterType;
  marketingModuleMap?: Record<string, WModuleGroupView>;
  navHTML: string;
  slug: string;
  topic: TopicType;
  userCtx: UserContext;
};

const Topic: React.FC<TopicProps> = ({
  expertAdviceFooter,
  marketingModuleMap,
  navHTML,
  topic,
  userCtx,
}) => {
  const isPostAuthNav3Enabled = useIsPostAuthNav3MobileOrDesktop(userCtx);
  const topicHref = `${EA_PATH_TOPIC}/${topic?.slug || ''}`;
  const breadcrumbItems = [{ to: EA_PATH, label: 'Expert Advice' }];

  const renderMarketingModuleSection = (i: 1 | 2 | 3 | 4 | 5): JSX.Element => {
    const widget = topic[`widget${i}`];
    const widgetTag = widget?.marketingModule?.tag;
    const widgetBusinessUnit = widget?.marketingModule?.businessUnit;
    const widgetView = marketingModuleMap && widgetTag ? marketingModuleMap[widgetTag] : undefined;

    const trackingData = {
      position: i,
      section: TRACKING_SECTIONS_EA.EA_TOPIC_PAGE,
      business_unit: widgetBusinessUnit || '',
    };
    return <MarketingModuleSection widget={widgetView} trackingData={trackingData} />;
  };

  return (
    <>
      <ExpertAdviceHeader breadcrumbs={breadcrumbItems} canonical={topicHref} topic={topic} />
      <Nav
        activePrimaryLink={PrimaryNavIdentifier.ADVICE}
        navHTML={navHTML}
        showPromo
        user={userCtx}
        disableSecondary
        disablePrimaryNavCollapse
      />
      <TopicPage
        expertAdviceFooter={expertAdviceFooter}
        isPostAuthNav3={isPostAuthNav3Enabled}
        renderMarketingModuleSection={renderMarketingModuleSection}
        topic={topic}
        userCtx={userCtx}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ params, req }) => {
  if (!params || !params.slug || Array.isArray(params.slug)) {
    throw badRequest('Missing or invalid slug');
  }

  const [expertAdviceFooter, navHTML, userCtx] = await Promise.all([
    getCachedExpertAdviceFooterData(req),
    fetchNavHTML(req),
    fetchUser(req),
  ]);

  const topicData = await getCachedTopicData(params.slug, req);
  if (!topicData.topic) {
    logger.error(`Page not found: ${params.slug}`);
    return { notFound: true };
  }

  const { topic } = topicData;

  const widgetTags = [
    topicData.topic.widget1?.marketingModule?.tag,
    topicData.topic.widget2?.marketingModule?.tag,
    topicData.topic.widget3?.marketingModule?.tag,
    topicData.topic.widget4?.marketingModule?.tag,
    topicData.topic.widget5?.marketingModule?.tag,
  ];
  const moduleGroupTags = widgetTags.filter(notEmpty);
  const marketingModuleMap = moduleGroupTags.length
    ? await fetchModuleGroups(req, moduleGroupTags)
    : undefined;

  return {
    props: JSON.parse(
      JSON.stringify({
        expertAdviceFooter,
        marketingModuleMap,
        navHTML,
        slug: params.slug,
        topic,
        userCtx,
      })
    ),
  };
};

export default Topic;
